import { Skeleton } from '@heroui/react';
import type { DataGridColumn } from '@heroui-pro/react';

/**
 * The row shape a skeleton `DataGrid` is fed. Only the key matters — every cell
 * renders a placeholder, so no field is ever read.
 */
export type DataGridSkeletonRow = { id: string };

/**
 * Placeholder rows for a register that is still loading.
 *
 * `DataGrid` owns its own `<tbody>`, so placeholder rows cannot be injected the
 * way `TableSkeletonRows` injects `<tr>`s into a hand-written table — they are
 * fed in as data instead. The grid keeps its real `<th>` headers either way,
 * which is what the loading-state standard asks for: fixed content stays, only
 * values and rows go grey.
 */
export const buildDataGridSkeletonRows = (
    rowCount: number,
    slot: string,
): DataGridSkeletonRow[] =>
    Array.from({ length: rowCount }, (_, index) => ({
        id: `${slot}-skeleton-${index}`,
    }));

/**
 * Mirror a register's real columns as skeleton columns: same ids, headers,
 * widths, alignment and pinning, every cell a grey bar.
 *
 * Keeping the column list identical is the point — the loaded table must not
 * reflow when the rows land. A pinned column stays pinned at the same offset,
 * and a column that opts out of resizing opts out here too, so a grid that
 * lays out through `allowsColumnResize` (the products table) sizes its
 * skeleton the same way. `isRowHeader` is dropped: a skeleton has no row
 * name to announce. DataGrid still makes the first column each row's header
 * when no column claims it (`@heroui-pro/react` 1.0.0-beta.9), so a
 * placeholder row starts with an empty row header rather than a plain cell.
 */
export const buildDataGridSkeletonColumns = <T,>(
    columns: DataGridColumn<T>[],
): DataGridColumn<DataGridSkeletonRow>[] =>
    columns.map((column) => ({
        id: column.id,
        header: column.header,
        align: column.align,
        width: column.width,
        minWidth: column.minWidth,
        maxWidth: column.maxWidth,
        allowsResizing: column.allowsResizing,
        pinned: column.pinned,
        headerClassName: column.headerClassName,
        cellClassName: column.cellClassName,
        cell: () => <Skeleton className="h-4 w-full rounded" />,
    }));
